import { crmGet, essDel, essGet, essGetBlob, essPatch, essPost, essPut, imsGet, hrmsGet } from "../api/api";
import { hasApprovalHistory } from "../modules/org-local-conveyance/constants/claimConstants";

const BASE = "/local-expense-claims";
const LINES = "/local-expense-claim-lines";

/** The metrics master is queried by category; this is the API's own value. */
const CURRENCY_METRIC_CATEGORY = "Currency";

/**
 * How many claims a list screen reads in one go.
 *
 * Every list filters and pages in the browser, because the search endpoint
 * offers no filter arguments — so a screen has to hold the rows it filters.
 * Ten would mean filtering ten of a hundred claims and calling it a report.
 */
const LIST_FETCH_SIZE = 200;


/**
 * Query string from params. A key with no value is left out — except one named
 * in `keepEmpty`, which is written as `key=`.
 *
 * The claim search needs that distinction: an ESS employee asks for their own
 * claims by sending an EMPTY searchType, and "empty" has to reach the URL as
 * `searchType=` rather than vanishing from it.
 */
function query(params, { keepEmpty = [] } = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    const empty = value === undefined || value === null || value === "";
    if (!empty) search.set(key, String(value));
    else if (keepEmpty.includes(key)) search.set(key, "");
  });
  return search.toString();
}

/** Rows live under data.content when paged, or data itself when not. */
function rowsOf(response, key) {
  const data = response?.data ?? {};
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.content)) return data.content;
  if (key && Array.isArray(data[key])) return data[key];
  return [];
}

/** Keys the caller left undefined are dropped so a PUT states only real changes. */
function compact(body) {
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined));
}

/**
 * A write that answered 200 with an error envelope.
 *
 * The transport rejects only on a non-OK HTTP status, so a body carrying
 * errorCode alongside a 200 — which is how a duplicate claim comes back —
 * RESOLVES, and the caller takes the rejection for a saved claim: the form
 * toasts "submitted" and navigates away from a claim that was never created.
 *
 * An errorCode in the body is a failure whatever the status line says. It is
 * thrown in the same shape parseResponse throws, so everything downstream
 * (duplicateWindowError, the forms' catch blocks) needs no special case.
 */
function assertWritten(envelope) {
  const errorCode = envelope?.errorCode;
  if (!errorCode) return envelope;

  const error = new Error(envelope.message || errorCode);
  error.errorCode = errorCode;
  error.data = envelope.data;
  throw error;
}

/* ----------------------------------------------------------------- claims */

/** POST /local-expense-claims/add — ESS07A. `payload.lineDetail` carries the trips. */
export async function createClaim(payload) {
  const response = assertWritten(await essPost(`${BASE}/add`, compact(payload), "ESS07A"));
  return response?.data ?? null;
}

/**
 * PUT /local-expense-claims/update/{claimId} — ESS07U.
 *
 * `lineDetail` is the claim's complete set of trips, not a delta: a line the
 * user removed is simply absent from the array.
 */
export async function updateClaim(claimId, payload) {
  const response = assertWritten(
    await essPut(`${BASE}/update/${claimId}`, compact(payload), "ESS07U")
  );
  return response?.data ?? null;
}

/** GET /local-expense-claims/{claimId} — ESS07S */
export async function getClaimById(claimId) {
  const response = await essGet(`${BASE}/${claimId}`, "ESS07S");
  return response?.data ?? null;
}

/** DELETE /local-expense-claims/delete/{claimId} — ESS07D. Cascades to its lines. */
export async function deleteClaim(claimId) {
  assertWritten(await essDel(`${BASE}/delete/${claimId}`, "ESS07D"));
  return claimId;
}



function claimPage(response) {
  const data = response?.data ?? {};
  const rows = Array.isArray(data.content) ? data.content : [];
  return { data, rows, totalCount: data.totalElements ?? rows.length };
}

/**
 * The employee's own claims. searchType is sent EMPTY — present in the query
 * string with no value — which is what selects findByTenantIdAndEmpId.
 */
export async function fetchMyClaims(empId, searchType = "") {
  const response = await essGet(
    `${BASE}/search/employee?${query(
      { empId, searchType, page: 0, size: LIST_FETCH_SIZE, ...NEWEST_FIRST },
      { keepEmpty: ["searchType"] }
    )}`,
    "ESS07S"
  );
  return rowsOf(response);
}

/* ------------------------------------------------------------ trip lines */

/**
 * GET /local-expense-claim-lines/search/claim?claimId= — ESS07S
 *
 * Sorted by the primary key server-side (the only column guaranteed to be
 * sortable), then ordered by line_no here, which is what the grid renders.
 */
export async function fetchTripLines(claimId) {
  const response = await essGet(
    `${LINES}/search/claim?${query({
      claimId,
      page: 0,
      size: 10,
      sortBy: "claimLineId",
      sortDir: "ASC",
    })}`,
    "ESS07S"
  );

  return rowsOf(response).sort((a, b) => Number(a.lineNo ?? 0) - Number(b.lineNo ?? 0));
}

/* ---------------------------------------------------------------- lookups */

/**
 * GET /saas/ims/product-metrics/search/category?category=Currency — IMS04S
 *
 * Feeds the currency selector next to every amount field. Only the id and the
 * abbreviation are kept: the id is what org_expense_claim.metric_id stores, the
 * abbreviation is what the selector shows.
 */
export async function fetchCurrencies() {
  const response = await imsGet(
    `/product-metrics/search/category?${query({
      category: CURRENCY_METRIC_CATEGORY,
      page: 0,
      size: 30,
      sortBy: "name",
      sortDir: "ASC",
    })}`,
    "IMS04S"
  );

  return rowsOf(response)
    .filter((row) => row.isActive !== false && row.metricId != null)
    .map((row) => ({
      metricId: row.metricId,
      abbreviation: row.abbreviation,
      name: row.name,
    }));
}

/** GET /saas/crm/customer-info/search — CRM01S. Called only when a trip line asks for it. */
export async function fetchCustomers() {
  const response = await crmGet(
    `/customer-info/search?${query({ page: 0, size: 200, sortBy: "companyName", sortDir: "ASC" })}`,
    "CRM01S"
  );

  return rowsOf(response, "customers")
    .map((row) => ({ id: row.customerId ?? row.id, name: row.companyName ?? row.name }))
    .filter((row) => row.id != null);
}

/** GET /saas/crm/customer-locations/search/customer — CRM04S. */
export async function fetchCustomerLocations(customerId) {
  const response = await crmGet(
    `/customer-locations/search/customer?${query({
      customerId,
      page: 0,
      size: 200,
      sortBy: "locationId",
      sortDir: "ASC",
    })}`,
    "CRM03S"
  );

  return rowsOf(response, "locations")
    .map((row) => ({ id: row.locationId ?? row.id, name: row.locationName ?? row.name }))
    .filter((row) => row.id != null);
}

/* ------------------------------------------------------- approval workflow */

/** UI action ids -> the values the action endpoint records. */
const APPROVER_ACTION_WIRE = { approve: "Approve", reject: "Reject", sendBack: "Send_back" };

/** Thrown by the readers/writers that have no endpoint yet. */
export class ApprovalApiUnavailableError extends Error {
  constructor() {
    super("APPROVAL_API_UNAVAILABLE");
    this.name = "ApprovalApiUnavailableError";
    this.messageKey = "conveyanceApproval.apiUnavailableError";
  }
}

/** Flip to true when the list/proof/export endpoints below are published. */
export const APPROVAL_API_READY = true;



/** GET /local-expense-claims/{claimId} — the claim, with its approval trail. */
export async function fetchClaimForApproval(claimId) {
  return getClaimById(claimId);
}

/** GET /saas/hrms/local-expense-policy-modes/search */
export async function fetchModes() {
  const response = await hrmsGet(
    `/local-expense-policy-modes/search?${query({ page: 0, size: 100 })}`
  );
  return rowsOf(response);
}

/** The claim's trip lines (resolves modeId from the master). */
export async function fetchClaimLines(claimId) {
  const [lines, modes] = await Promise.all([
    fetchTripLines(claimId),
    fetchModes().catch((e) => {
      console.error("Failed to fetch modes", e);
      return [];
    })
  ]);
  
  return lines.map((line) => {
    if (line.modeId != null && !line.modeTransport) {
      const mode = modes.find(m => String(m.id ?? m.modeId) === String(line.modeId));
      if (mode) {
        line.modeTransport = mode.modeName ?? mode.name ?? mode.modeTransport ?? mode.mode;
      }
    }
    return line;
  });
}

/** POST /local-expense-claims/{claimId}/action — ESS07U. Approve / Reject / Send back. */
export async function submitApproverAction({ claimId, action, reason }) {
  const response = assertWritten(
    await essPost(
      `${BASE}/${claimId}/action`,
      { action: APPROVER_ACTION_WIRE[action] ?? action, reasonComment: reason || null },
      "ESS07U"
    )
  );
  return response?.data ?? null;
}

/**
 * GET /local-expense-claims/{claimId}/action — ESS07S.
 *
 * The claim's approval trail: one row per Approve / Reject / Send back, each
 * carrying the approver's comment.
 *
 * Only a fallback, and deliberately so — see resolveClaimActions. Where the
 * deployed build has no reader on this path, the write mapping answers a GET
 * with INVALID_METHOD_USED, so nothing calls this blind.
 *
 * Oldest first, so the trail reads in the order the decisions were taken.
 */
export async function fetchClaimActions(claimId) {
  const response = await essGet(`${BASE}/${claimId}/action`, "ESS07S");
  return rowsOf(response, "actions").sort(
    (a, b) => new Date(a.actionOn ?? 0) - new Date(b.actionOn ?? 0)
  );
}

/**
 * A claim's approval trail, wherever it can be had.
 *
 * GET /local-expense-claims/{claimId} already returns the trail as `actions`,
 * so that is the source: it costs no extra request and it is what every claim
 * screen already holds. The action endpoint is only asked when the claim came
 * back WITHOUT a trail and its status says one must exist — a claim nobody has
 * acted on has nothing to fetch, and asking anyway would put a failed request
 * on every draft.
 *
 * Never throws: a claim's own detail is what its screen is for, and it must
 * stay usable whether or not the trail could be resolved.
 */
export async function resolveClaimActions(claim) {
  if (Array.isArray(claim?.actions) && claim.actions.length > 0) return claim.actions;
  if (claim?.claimId == null || !hasApprovalHistory(claim?.claimStatus)) return [];
  try {
    return await fetchClaimActions(claim.claimId);
  } catch (error) {
    console.warn("Approval trail unavailable:", error);
    return [];
  }
}

/** PATCH /local-expense-claims/mark-processed — ESS07U. Finance bulk flip to Processed. */
export async function markClaimsProcessed(claimIds) {
  const response = assertWritten(
    await essPatch(`${BASE}/mark-processed`, { claimIds }, "ESS07U")
  );
  return response?.data ?? null;
}



/**
 * Newest claim first, on both approver reports.
 *
 * claim_id is the insert order, so sorting by it descending is "most recently
 * raised at the top" — and it is stable, which a date shared by several claims
 * raised on the same day is not. The SERVER sorts (buildPageable reads these),
 * so page 1 really is the newest ten, rather than ten arbitrary rows put in
 * order after they arrive.
 */
const NEWEST_FIRST = { sortBy: "claimId", sortDir: "DESC" };

/**
 * The Reporting Manager's queue: the claims currently PENDING WITH the
 * signed-in approver, per findByTenantIdAndPendingWithEmp. Nothing is narrowed
 * here — the repository query is what decides whose claims these are.
 */
export async function fetchTeamClaims({ empId, ...filters } = {}) {
  const response = await essGet(
    `${BASE}/search/employee?${query({
      empId,
      searchType: "Team",
      page: 0,
      size: LIST_FETCH_SIZE,
      ...NEWEST_FIRST,
      ...filters,
    })}`,
    "ESS07S"
  );
  const { data, rows, totalCount } = claimPage(response);
  return {
    rows,
    totalCount,
    pendingCount: data.pendingCount ?? 0,
    filterOptions: data.filterOptions ?? { submitters: [], months: [] },
  };
}
/**
 * The Finance register: every claim in the tenant, per findByTenantId. empId
 * still travels — it identifies the caller, not the rows.
 */
export async function fetchAllClaims({ empId, ...filters } = {}) {
  const response = await essGet(
    `${BASE}/search/employee?${query({
      empId,
      searchType: "Finance",
      page: 0,
      size: LIST_FETCH_SIZE,
      ...NEWEST_FIRST,
      ...filters,
    })}`,
    "ESS07S"
  );
  const { data, rows, totalCount } = claimPage(response);
  return {
    rows,
    totalCount,
    totals: data.totals ?? [],
    filterOptions: data.filterOptions ?? { submitters: [], months: [] },
  };
}

/*
 * Proofs have no endpoint of their own: every one is stored in the claim's
 * `attachments` list — tagged with its claimLineId for a per-km trip, null for
 * a fixed monthly / maintenance claim. A save sends them (on the claim, or
 * inside each lineDetail entry for per km) and GET /{claimId} returns them all;
 * the trip-line endpoints carry none.
 */

/**
 * GET /local-expense-claims/export — ESS07S.
 * Server-side CSV/XLSX of every claim matching the current filters.
 * Returns { blob, fileName } — the caller creates the download link.
 */
export async function exportClaims(params = {}) {
  const { blob, contentDisposition } = await essGetBlob(
    `${BASE}/export?${query(params)}`,
    "ESS07S"
  );
  const match = contentDisposition && contentDisposition.match(/filename\*?=['"']?([^;'"]+)/);
  const fileName = match ? decodeURIComponent(match[1].trim()) : "conveyance-claims.csv";
  return { blob, fileName };
}

export const conveyanceClaimService = {
  createClaim,
  updateClaim,
  getClaimById,
  deleteClaim,
  fetchMyClaims,
  fetchTripLines,
  fetchCurrencies,
  fetchCustomers,
  fetchCustomerLocations,
  fetchClaimForApproval,
  fetchClaimLines,
  submitApproverAction,
  fetchClaimActions,
  resolveClaimActions,
  markClaimsProcessed,
  fetchTeamClaims,
  fetchAllClaims,
  exportClaims,
  APPROVAL_API_READY,
};

export default conveyanceClaimService;
